import {
  containsCoordinate,
  createCameraState,
  createLngLat,
  type CameraState,
  type GeoBounds,
} from '../domain/coordinates.ts';

export type SavedViewScope = 'candidate' | 'local' | 'production' | PreviewSavedViewScope;

interface PreviewSavedViewScope {
  readonly previewPullRequest: number;
}

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface SavedViewStore {
  load(): CameraState | undefined;
  save(camera: CameraState): void;
}

function storageKey(scope: SavedViewScope): string {
  if (scope === 'production') return 'zbt.view.v1';
  if (scope === 'candidate') return 'zbt.next.view.v1';
  if (scope === 'local') return 'zbt.dev.view.v1';
  if (!Number.isSafeInteger(scope.previewPullRequest) || scope.previewPullRequest <= 0) {
    throw new RangeError('preview pull request must be a positive safe integer');
  }
  return `zbt.pr.${String(scope.previewPullRequest)}.view.v1`;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function optionalCameraNumber(
  record: Readonly<Record<string, unknown>>,
  field: 'bearing' | 'pitch',
): number | undefined {
  const value = record[field];
  if (value === undefined) return 0;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function parseStoredCamera(value: string, bounds: GeoBounds): CameraState | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed)) return undefined;
    const { lat, lng, zoom } = parsed;
    const bearing = optionalCameraNumber(parsed, 'bearing');
    const pitch = optionalCameraNumber(parsed, 'pitch');
    if (
      typeof lng !== 'number' ||
      !Number.isFinite(lng) ||
      typeof lat !== 'number' ||
      !Number.isFinite(lat) ||
      typeof zoom !== 'number' ||
      !Number.isFinite(zoom) ||
      bearing === undefined ||
      pitch === undefined ||
      bearing < -180 ||
      bearing >= 180 ||
      pitch < 0 ||
      pitch > 60
    ) {
      return undefined;
    }
    const center = createLngLat(lng, lat);
    if (!containsCoordinate(bounds, center)) return undefined;
    return createCameraState({ center, zoom, bearing, pitch });
  } catch {
    return undefined;
  }
}

function rounded(value: number, decimalPlaces: number): number {
  const result = Number(value.toFixed(decimalPlaces));
  return Object.is(result, -0) ? 0 : result;
}

export function createSavedViewStore({
  bounds,
  scope,
  storage,
}: {
  readonly bounds: GeoBounds;
  readonly scope: SavedViewScope;
  readonly storage: KeyValueStorage;
}): SavedViewStore {
  const key = storageKey(scope);
  return Object.freeze({
    load(): CameraState | undefined {
      try {
        const value = storage.getItem(key);
        return value === null ? undefined : parseStoredCamera(value, bounds);
      } catch {
        return undefined;
      }
    },
    save(camera: CameraState): void {
      if (!containsCoordinate(bounds, camera.center)) return;
      try {
        storage.setItem(
          key,
          JSON.stringify({
            lng: rounded(camera.center[0], 5),
            lat: rounded(camera.center[1], 5),
            zoom: rounded(camera.zoom, 2),
            bearing: rounded(camera.bearing, 1),
            pitch: rounded(camera.pitch, 1),
          }),
        );
      } catch {
        // Saved views are a best-effort convenience and never block map use.
      }
    },
  });
}
